import { ChartVisualization, TextVisualization, StatsVisualization, HeatmapVisualization, ThreadVisualization, VisualizationType, Table } from '../types'

/**
 * Project a table down to only the columns the visualization reads, so that
 * postMessage structured-clones a fraction of the table into the worker
 * (issue #122). The worker resolves columns by name (getTableColumn), so the
 * projection is transparent to it. Display and donation data are unaffected.
 */
export function selectVisualizationColumns (table: Table, visualization: VisualizationType): Table {
  const columns = visualizationColumns(visualization).filter((column) => table.head.cells.includes(column))
  const indices = columns.map((column) => table.head.cells.indexOf(column))
  return {
    id: table.id,
    head: { cells: columns },
    body: {
      rows: table.body.rows.map((row) => ({
        id: row.id,
        cells: indices.map((index) => row.cells[index])
      }))
    }
  }
}

function visualizationColumns (visualization: VisualizationType): string[] {
  const columns = new Set<string>()

  if (['line', 'bar', 'area'].includes(visualization.type)) {
    const chart = visualization as ChartVisualization
    columns.add(chart.group.column)
    for (const value of chart.values) {
      if (value.column !== undefined) columns.add(value.column)
      if (value.group_by !== undefined) columns.add(value.group_by)
      if (value.z !== undefined) columns.add(value.z)
    }
  }

  if (visualization.type === 'wordcloud') {
    const text = visualization as TextVisualization
    columns.add(text.textColumn)
    if (text.valueColumn !== undefined) columns.add(text.valueColumn)
    if (text.excludeColumn !== undefined) columns.add(text.excludeColumn)
  }

  if (visualization.type === 'stats') {
    const stats = visualization as StatsVisualization
    for (const tile of stats.tiles) {
      if (tile.column !== undefined) columns.add(tile.column)
    }
  }

  if (visualization.type === 'heatmap') {
    const heatmap = visualization as HeatmapVisualization
    columns.add(heatmap.dateColumn)
    if (heatmap.valueColumn !== undefined) columns.add(heatmap.valueColumn)
  }

  if (visualization.type === 'thread') {
    const thread = visualization as ThreadVisualization
    if (thread.groupColumn !== undefined) columns.add(thread.groupColumn)
    if (thread.titleColumn !== undefined) columns.add(thread.titleColumn)
    columns.add(thread.roleColumn)
    columns.add(thread.textColumn)
    if (thread.timeColumn !== undefined) columns.add(thread.timeColumn)
    if (thread.badgeColumn !== undefined) columns.add(thread.badgeColumn)
  }

  return Array.from(columns)
}
